import { cn } from '@core/utils';

import type { HouseholdSize } from '../_hooks/useMedianIncomeState';
import type { MedianIncomeYear } from '../constants';
import { HOUSEHOLD_OPTIONS,MEDIAN_INCOME_BY_YEAR } from '../constants';

interface TierTableProps {
  year: MedianIncomeYear;
  /** 계산기에서 선택한 가구원수(1~7). 이 컬럼 하나만 노출한다. */
  householdSize: HouseholdSize;
  /** 계산된 가장 가까운 구간(%). 매칭 row 를 강조한다. null 이면 강조 없음. */
  activeTier: number | null;
  /** Row 클릭 시 우측 계산기에 해당 % 값을 반영한다. */
  onTierClick: (percent: number) => void;
}

/** 항상 강조되는 기준선(중위소득 100%). */
const BASELINE_TIER = 100;
const formatNumber = (n: number) => Math.round(n).toLocaleString('ko-KR');

/**
 * ④ 표1 — 250%~50% 구간 × 가구원수 1열.
 * - 사용자가 계산기에서 고른 가구원수에 해당하는 컬럼 하나만 동적으로 노출한다.
 * - `BASELINE_TIER`(100%) row 는 항상 강조, `activeTier` 와 일치하는 row 는 추가 강조.
 */
export const TierTable = ({
  year,
  householdSize,
  activeTier,
  onTierClick,
}: TierTableProps) => {
  const { householdBaselines, tiers, source } = MEDIAN_INCOME_BY_YEAR[year];
  const householdIndex = householdSize - 1;
  const baselineWon = householdBaselines[householdIndex] ?? 0;
  const householdLabel =
    HOUSEHOLD_OPTIONS[householdIndex]?.label ?? `${householdSize}인`;

  return (
    <section
      id="median-income-tier-table"
      className="flex scroll-mt-80 flex-col gap-12 mobile-tablet:scroll-mt-64 mobile-tablet:px-16"
    >
      <h2 className="font-700 text-Gray-900 typo-h5 mobile-tablet:typo-h6">
        {year}년 기준 중위소득
      </h2>
      <p className="text-Gray-700 typo-body4">
        {year} {householdLabel} 가구 기준 100% 금액은{' '}
        <strong className="font-700 text-Gray-900">
          월 {formatNumber(baselineWon)}원
        </strong>
        이며, 중위소득 % 구간별로 신청 가능한 복지정책이 달라집니다.
      </p>
      <div className="overflow-hidden rounded-small border-1 border-Gray-200">
        <table className="w-full border-collapse text-Gray-800 typo-body4">
          <caption className="sr-only">
            {year}년 {householdLabel} 가구 기준 중위소득 구간별 월 금액 (출처{' '}
            {source})
          </caption>
          <thead>
            <tr className="bg-Gray-50 text-Gray-700">
              <th
                scope="col"
                className="border-b-1 border-Gray-200 p-12 text-center font-600 typo-caption1"
              >
                중위소득 %
              </th>
              <th
                scope="col"
                className="border-b-1 border-Gray-200 p-12 text-center font-600 typo-caption1"
              >
                {householdLabel} (원)
              </th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((percent) => {
              const isBaseline = percent === BASELINE_TIER;
              const isActive = activeTier === percent;

              return (
                <tr
                  key={percent}
                  className={cn(
                    'cursor-pointer border-t-1 border-Gray-100 transition-colors',
                    {
                      'bg-Y-50': isBaseline && !isActive,
                      'bg-B-50': isActive,
                      'hover:bg-B-25': !isActive && !isBaseline,
                    },
                  )}
                  onClick={() => onTierClick(percent)}
                >
                  <td
                    className={cn(
                      'p-12 text-center font-700',
                      isActive ? 'text-B-600' : 'text-Gray-900',
                    )}
                  >
                    {percent}%
                  </td>
                  <td
                    className={cn(
                      'p-12 text-center tabular-nums',
                      isActive && 'font-700 text-B-600',
                    )}
                  >
                    {formatNumber((baselineWon * percent) / 100)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td
                colSpan={2}
                className="border-t-1 border-Gray-200 bg-Gray-25 px-12 py-8 text-center text-Gray-600 typo-caption2"
              >
                단위: 월/원 · 출처 {source}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
};
